import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtIST } from "@/lib/datetime";
import { Car, Plus } from "@phosphor-icons/react";

export default function Vehicles() {
    const [items, setItems] = useState([]);
    const [gates, setGates] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ vehicle_number: "", vehicle_type: "car", driver_name: "", driver_phone: "", purpose: "", gate_id: "" });

    const load = async () => setItems((await api.get("/gate-check/vehicles")).data);
    useEffect(() => { load(); api.get("/gate-check/gates").then((r) => { setGates(r.data); if (r.data.length) setForm((f) => ({ ...f, gate_id: r.data[0].id })); }); }, []);

    const add = async () => {
        try {
            await api.post("/gate-check/vehicles", form);
            toast.success("Vehicle logged");
            setForm({ ...form, vehicle_number: "", driver_name: "", driver_phone: "", purpose: "" });
            setOpen(false); load();
        } catch { toast.error("Failed"); }
    };

    return (
        <div>
            <PageHeader
                overline="Gate Ops"
                title="Vehicle Log"
                subtitle="All vehicles entering/leaving premises. Auto-linked to visitor check-ins."
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="btn-log-vehicle" className="h-11 bg-brand hover:bg-brand/90 text-white">
                                <Plus size={16} weight="bold" className="mr-2" /> Log Vehicle
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-display">Log New Vehicle</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <div>
                                    <Label className="overline text-[10px]">Vehicle Number *</Label>
                                    <Input value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
                                        data-testid="input-veh-number" className="h-11 mt-1" placeholder="KA-01-AB-1234" />
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Type</Label>
                                    <Select value={form.vehicle_type} onValueChange={(v) => setForm({ ...form, vehicle_type: v })}>
                                        <SelectTrigger className="h-11 mt-1" data-testid="select-veh-type"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["car", "bike", "truck", "delivery", "cab"].map((t) => (
                                                <SelectItem key={t} value={t}>{t.toUpperCase()}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label className="overline text-[10px]">Driver</Label>
                                        <Input value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                                            data-testid="input-veh-driver" className="h-11 mt-1" />
                                    </div>
                                    <div>
                                        <Label className="overline text-[10px]">Driver Phone</Label>
                                        <Input value={form.driver_phone} onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
                                            data-testid="input-veh-phone" className="h-11 mt-1" />
                                    </div>
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Purpose</Label>
                                    <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                                        data-testid="input-veh-purpose" className="h-11 mt-1" />
                                </div>
                                <div>
                                    <Label className="overline text-[10px]">Gate *</Label>
                                    <Select value={form.gate_id} onValueChange={(v) => setForm({ ...form, gate_id: v })}>
                                        <SelectTrigger className="h-11 mt-1" data-testid="select-veh-gate"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {gates.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button onClick={add} data-testid="btn-save-vehicle"
                                    className="w-full h-11 bg-brand hover:bg-brand/90 text-white">Save Vehicle</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                }
            />
            <div className="p-6">
                <Card className="tech-card overflow-hidden">
                    <div className="divide-y divide-border">
                        {items.length === 0 && <div className="p-8 text-center text-muted-foreground">No vehicles logged.</div>}
                        {items.map((v) => (
                            <div key={v.id} className="px-5 py-4 flex items-center gap-4" data-testid={`row-veh-${v.id}`}>
                                <Car size={22} weight="duotone" className="text-brand" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold text-lg tracking-tight">{v.vehicle_number}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {v.vehicle_type?.toUpperCase()} · Driver: {v.driver_name || "—"} · Purpose: {v.purpose || "—"} · Gate: {v.gate_name}
                                    </div>
                                </div>
                                <span className="text-xs text-muted-foreground">{fmtIST(v.created_at)}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
